import { Trash2 } from 'lucide-react'
import React from 'react'

import { Button } from '@/components/atoms/button'
import { Textarea } from '@/components/atoms/textarea'
import { Slider } from '@/components/molecules/slider'
import { SingleSelect } from '@/components/molecules/single-select'

import {
  ImageEditorEngine,
  ImageEditorSelectedLayer,
} from '../image-editor.types'
import { FONTS } from '../picture-studio-engine'
import { ColorPicker } from './color-picker'
import { useImageEditorPortalContainer } from './portal-context'

interface PanelSelectedProps {
  selected: ImageEditorSelectedLayer
  engine: ImageEditorEngine
}

const FILL_ITEMS = [
  { label: 'Outline only', value: 'stroke' },
  { label: 'Solid fill', value: 'fill' },
]

export function PanelSelected({ selected, engine }: PanelSelectedProps) {
  const container = useImageEditorPortalContainer()
  return (
    <div>
      {selected.type === 'text' && (
        <div className="mb-3.5">
          <span className="mb-1 block text-xs text-light-4">Text</span>
          <Textarea
            className="min-h-[52px]"
            value={selected.text ?? ''}
            onChange={(e) => engine.setSelectedText(e.target.value)}
          />
          <span className="mb-1 mt-2.5 block text-xs text-light-4">Font</span>
          <SingleSelect
            items={FONTS}
            value={selected.font ?? ''}
            onValueChange={engine.setSelectedFont}
            container={container}
          />
          <div className="mb-1 mt-2.5 flex justify-between text-xs text-light-4">
            <span>Size</span>
            <span>{selected.size}px</span>
          </div>
          <Slider
            min={10}
            max={240}
            step={1}
            value={[selected.size ?? 24]}
            onValueChange={([v]) => engine.setSelectedTextSize(v)}
          />
          <div className="mb-1 mt-2.5 flex justify-between text-xs text-light-4">
            <span>Box size</span>
            <span>{selected.boxWidth}px</span>
          </div>
          <Slider
            min={20}
            max={2000}
            step={1}
            value={[selected.boxWidth ?? 200]}
            onValueChange={([v]) => engine.setSelectedBoxWidth(v)}
          />
        </div>
      )}

      {selected.type === 'shape' && (
        <div className="mb-3.5">
          <span className="mb-1 block text-xs text-light-4">Fill style</span>
          <SingleSelect
            items={FILL_ITEMS}
            value={selected.fill ?? 'stroke'}
            onValueChange={(v) =>
              engine.setSelectedShapeFill(v as 'stroke' | 'fill')
            }
            container={container}
          />
          <span className="mb-1 mt-2.5 block text-xs text-light-4">
            Thickness
          </span>
          <Slider
            min={1}
            max={40}
            step={1}
            value={[selected.width ?? 6]}
            onValueChange={([v]) => engine.setSelectedWidth(v)}
          />
        </div>
      )}

      {selected.type === 'stroke' && (
        <div className="mb-3.5">
          <span className="mb-1 block text-xs text-light-4">Brush size</span>
          <Slider
            min={1}
            max={60}
            step={1}
            value={[selected.width ?? 8]}
            onValueChange={([v]) => engine.setSelectedWidth(v)}
          />
        </div>
      )}

      {selected.type !== 'sticker' && (
        <ColorPicker
          value={selected.color ?? '#d24b2f'}
          onChange={engine.setSelectedColor}
        />
      )}

      <div className="my-3.5 h-px bg-neutral-1" />

      <div className="flex flex-col gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={engine.bringSelectedToFront}
        >
          Bring to front
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={engine.sendSelectedToBack}
        >
          Send to back
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={engine.duplicateSelected}
        >
          Duplicate
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={engine.deleteSelected}
          prefixIcon={<Trash2 className="h-4 w-4" />}
          className="text-danger-1"
        >
          Delete layer
        </Button>
      </div>
    </div>
  )
}
